<template>
  <div class="management-potential-chart">
    <h3 class="management-potential-chart__title">
      {{ title }}
    </h3>

    <div class="management-potential-chart__legend">
      <div class="management-potential-chart__legend-item">
        <span
            class="management-potential-chart__legend-mark"
            :style="{ backgroundColor: previousColor }"
        ></span>

        <span>{{ previousLabel }}</span>
      </div>

      <div class="management-potential-chart__legend-item">
        <span
            class="management-potential-chart__legend-mark"
            :style="{ backgroundColor: currentColor }"
        ></span>

        <span>{{ currentLabel }}</span>
      </div>
    </div>

    <div class="management-potential-chart__canvas-wrap">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  Tooltip,
} from 'chart.js'

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip
)

const WHITE_COLOR = '#FFFFFF'
const GRID_COLOR = '#F0F0F0'
const TEXT_COLOR = 'rgba(0, 0, 0, 0.45)'

const barValuePlugin = {
  id: 'barValuePlugin',

  afterDatasetsDraw(chart) {
    const ctx = chart.ctx

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)

      meta.data.forEach((bar, index) => {
        const value = dataset.data[index]

        if (value === null || value === undefined) {
          return
        }

        const props = bar.getProps(['x', 'y', 'base'], true)

        ctx.save()
        ctx.fillStyle = WHITE_COLOR
        ctx.font = '600 11px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'

        const barHeight = Math.abs(props.base - props.y)
        const labelY = barHeight > 22
            ? props.base - 12
            : props.y - 8

        ctx.fillText(String(value), props.x, labelY)
        ctx.restore()
      })
    })
  },
}

export default {
  name: 'ManagementPotentialCompareChart',

  props: {
    title: {
      type: String,
      default: '',
    },

    previousLabel: {
      type: String,
      default: 'Было',
    },

    currentLabel: {
      type: String,
      default: 'Стало',
    },

    previousColor: {
      type: String,
      default: '#D5DBE2',
    },

    currentColor: {
      type: String,
      default: '#84A2FF',
    },

    items: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    let chartInstance = null

    const normalizedItems = computed(() => {
      return (Array.isArray(props.items) ? props.items : []).map((item, index) => ({
        key: item?.key || `item-${index}`,
        label: item?.label || '',
        previousValue: toNumber(item?.previousValue),
        currentValue: toNumber(item?.currentValue),
      }))
    })

    const labels = computed(() => {
      return normalizedItems.value.map((item) => item.label)
    })

    const previousValues = computed(() => {
      return normalizedItems.value.map((item) => item.previousValue)
    })

    const currentValues = computed(() => {
      return normalizedItems.value.map((item) => item.currentValue)
    })

    const maxValue = computed(() => {
      const values = [
        ...previousValues.value,
        ...currentValues.value,
      ]

      const max = Math.max(...values, 0)

      if (max <= 10) {
        return 10
      }

      return Math.ceil((max + 5) / 5) * 5
    })

    const buildChart = () => {
      if (!chartCanvas.value) {
        return
      }

      destroyChart()

      chartInstance = new Chart(chartCanvas.value, {
        type: 'bar',
        data: {
          labels: labels.value,
          datasets: [
            {
              label: props.previousLabel,
              data: previousValues.value,
              backgroundColor: props.previousColor,
              borderRadius: 4,
              borderSkipped: false,
              categoryPercentage: 0.9,
              barPercentage: 0.9,
            },
            {
              label: props.currentLabel,
              data: currentValues.value,
              backgroundColor: props.currentColor,
              borderRadius: 4,
              borderSkipped: false,
              categoryPercentage: 0.9,
              barPercentage: 0.9,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          layout: {
            padding: {
              top: 4,
              right: 0,
              bottom: 0,
              left: 0,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            datalabels: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label(context) {
                  return `${context.dataset.label}: ${context.parsed.y}`
                },
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: true,
                color: GRID_COLOR,
                drawTicks: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: TEXT_COLOR,
                font: {
                  size: 12,
                },
              },
            },
            y: {
              beginAtZero: true,
              max: maxValue.value,
              ticks: {
                stepSize: 5,
                color: TEXT_COLOR,
                font: {
                  size: 12,
                },
              },
              grid: {
                color: GRID_COLOR,
                drawTicks: false,
              },
              border: {
                display: false,
              },
            },
          },
        },
        plugins: [barValuePlugin],
      })
    }

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy()
        chartInstance = null
      }
    }

    onMounted(() => {
      nextTick(() => {
        buildChart()
      })
    })

    onBeforeUnmount(() => {
      destroyChart()
    })

    watch(
        () => [
          props.items,
          props.previousLabel,
          props.currentLabel,
          props.previousColor,
          props.currentColor,
        ],
        () => {
          nextTick(() => {
            buildChart()
          })
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
    }
  },
}

function toNumber(value) {
  const number = Number(value)

  return Number.isFinite(number) ? number : 0
}
</script>

<style scoped lang="scss">
.management-potential-chart {
  width: 100%;
  min-width: 0;
}

.management-potential-chart__title {
  margin: 0 0 20px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.management-potential-chart__legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 20px;
}

.management-potential-chart__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.management-potential-chart__legend-mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.management-potential-chart__canvas-wrap {
  position: relative;
  width: 100%;
  height: 230px;
  min-width: 0;
}

@media (max-width: $mobile-breakpoint) {
  .management-potential-chart__canvas-wrap {
    height: 260px;
  }

  .management-potential-chart__legend {
    gap: 10px;
  }
}
</style>